import React, { useRef, useState } from "react";
import {
  AlertCircle,
  Circle,
  Loader2,
  Monitor,
  MonitorIcon,
  MonitorOff,
} from "lucide-react";
import ControlBar from "./ControlBarOption";
import RemoteVideo from "./RemoteVideo";
import "./VideoConsultation.scss";
import { message } from "antd";
import ConsultationHeader from "./ConsultationHeader";
import { set } from "date-fns";

import ConsultationSidePanel from "./consultation-details/ConsultationSidePanel";

const VideoConsultation = ({
  config,
  role = "Patient",
  headerData,
  onLeave,
  onJoin,
}) => {
  const [participantCount, setParticipantCount] = React.useState(1);
  const [audioDevices, setAudioDevices] = React.useState([]);
  const [videoDevices, setVideoDevices] = React.useState([]);
  const [participants, setParticipants] = React.useState([]);
  const zoomClientRef = useRef(null);
  const streamRef = useRef(null);

  const [client, setClient] = React.useState(null);
  const [stream, setStream] = React.useState(null);
  const [mediaState, setMediaState] = React.useState({
    video: false,
    audio: false,
    screen: false,
  });
  const [screenshareEnabled, setScreenshareEnabled] = useState(false);
  const [audioDisabled, setAudioDisabled] = useState(false);
  const [isInitializing, setIsInitializing] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [activeSharingUserId, setActiveSharingUserId] = React.useState(null);
  const [sharerName, setSharerName] = React.useState("");
  const [pipPosition, setPipPosition] = React.useState({ x: 10, y: 10 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState({ x: 0, y: 0 });
  const [patientInfo, setPatientInfo] = React.useState({ name: "", id: "" });
  const [browserInfo, setBrowserInfo] = React.useState({
    isWebCodecs: false,
    isSafari: false,
    isIOS: false,
  });
  const [screenSubscribed, setScreenSubscribed] = React.useState(false);
  const [showSidePanel, setShowSidePanel] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("info");
  // const [showSidePanel, setShowSidePanel] = React.useState(false);
  // Removed sidePanelContent state as it handled inside ConsultationSidePanel

  const shareContainerRef = React.useRef(null);
  const screenShareVideoRef = React.useRef(null);
  const screenShareCanvasRef = React.useRef(null);
  const shareElementRef = React.useRef(null);
  const pipRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [localScreenStream, setLocalScreenStream] = React.useState(null);
  const isScreenSharingRef = React.useRef(false); // Track screen sharing state
  const isSubscribingRef = React.useRef(false);
  const subscriptionTimeoutRef = React.useRef(null);
  const currentSharingUserIdRef = React.useRef(null);

  const isDoctor = role === "Web_Doctor";
  const canShareScreen = isDoctor;
  const canLeave = isDoctor;

  React.useEffect(() => {
    streamRef.current = stream;
  }, [stream]);
  // Detect browser capabilities
  React.useEffect(() => {
    const detectBrowser = () => {
      const ua = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(ua);
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
      const hasWebCodecs = "VideoEncoder" in window && "VideoDecoder" in window;

      console.log("Browser Detection:", {
        isIOS,
        isSafari,
        hasWebCodecs,
        userAgent: ua,
      });

      setBrowserInfo({
        isWebCodecs: hasWebCodecs,
        isSafari,
        isIOS,
      });
    };

    detectBrowser();
  }, []);

  React.useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      setAudioDevices(devices.filter((d) => d.kind === "audioinput"));
      setVideoDevices(devices.filter((d) => d.kind === "videoinput"));
    });
  }, []);

  // Snap to corners function
  const snapToCorner = (
    x,
    y,
    pipWidth,
    pipHeight,
    containerWidth,
    containerHeight
  ) => {
    const margin = 10;
    const corners = [
      { x: margin, y: margin },
      { x: containerWidth - pipWidth - margin, y: margin },
      { x: margin, y: containerHeight - pipHeight - margin },
      {
        x: containerWidth - pipWidth - margin,
        y: containerHeight - pipHeight - margin,
      },
    ];

    let closestCorner = corners[0];
    let minDistance = Number.MAX_SAFE_INTEGER;

    corners.forEach((corner) => {
      const distance = Math.sqrt(
        Math.pow(corner.x - x, 2) + Math.pow(corner.y - y, 2)
      );
      if (distance < minDistance) {
        minDistance = distance;
        closestCorner = corner;
      }
    });

    return closestCorner;
  };

  // PIP Drag handlers
  const handleMouseDown = (e) => {
    if (!pipRef.current) return;
    const rect = pipRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setDragOffset({ x: offsetX, y: offsetY });
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = React.useCallback(
    (e) => {
      if (!isDragging || !pipRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const pipRect = pipRef.current.getBoundingClientRect();
      let newX = e.clientX - containerRect.left - dragOffset.x;
      let newY = e.clientY - containerRect.top - dragOffset.y;
      const margin = 10;
      newX = Math.max(
        margin,
        Math.min(containerRect.width - pipRect.width - margin, newX)
      );
      newY = Math.max(
        margin,
        Math.min(containerRect.height - pipRect.height - margin, newY)
      );
      setPipPosition({ x: newX, y: newY });
    },
    [isDragging, dragOffset]
  );

  const handleMouseUp = React.useCallback(() => {
    if (!isDragging || !pipRef.current || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const pipRect = pipRef.current.getBoundingClientRect();
    const snappedPosition = snapToCorner(
      pipPosition.x,
      pipPosition.y,
      pipRect.width,
      pipRect.height,
      containerRect.width,
      containerRect.height
    );
    setPipPosition(snappedPosition);
    setIsDragging(false);
  }, [isDragging, pipPosition]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    const updatePipPosition = () => {
      if (!pipRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const pipRect = pipRef.current.getBoundingClientRect();
      const margin = 10;
      const defaultPosition = {
        x: margin,
        y: margin,
      };
      setPipPosition(defaultPosition);
    };
    setTimeout(updatePipPosition, 100);
    window.addEventListener("resize", updatePipPosition);
    return () => window.removeEventListener("resize", updatePipPosition);
  }, []);

  React.useEffect(() => {
    const count = participants.length;
    setParticipantCount(count);
  }, [participants]);

  React.useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("userData")) || {};
    const storedName = userData.profileName || "guest";
    const storedId = userData.userId || "0000";
    if (storedName && storedId) {
      setPatientInfo({ name: storedName, id: storedId });
    }
  }, []);

  // Cleanup screen share elements
  // const cleanupScreenShareElements = React.useCallback(() => {
  //   console.log("🧹 Cleaning up screen share elements");

  //   if (screenShareVideoRef.current) {
  //     try {
  //       screenShareVideoRef.current.srcObject = null;
  //       screenShareVideoRef.current.remove();
  //     } catch (e) {
  //       console.warn("Error removing video element:", e);
  //     }
  //     screenShareVideoRef.current = null;
  //   }

  //   if (screenShareCanvasRef.current) {
  //     try {
  //       screenShareCanvasRef.current.remove();
  //     } catch (e) {
  //       console.warn("Error removing canvas element:", e);
  //     }
  //     screenShareCanvasRef.current = null;
  //   }

  //   if (shareElementRef.current && shareElementRef.current.parentNode) {
  //     try {
  //       shareElementRef.current.remove();
  //     } catch (e) {
  //       console.warn("Error removing share element:", e);
  //     }
  //     shareElementRef.current = null;
  //   }

  //   if (shareContainerRef.current) {
  //     shareContainerRef.current.innerHTML = "";
  //   }
  // }, []);

  const cleanupScreenShareElements = React.useCallback(() => {
    console.log("🧹 Cleaning up screen share elements");
  
    // Safely cleanup video element
    if (screenShareVideoRef.current) {
      try {
        screenShareVideoRef.current.srcObject = null;
        // Only remove if it's in the DOM
        if (screenShareVideoRef.current.parentNode) {
          screenShareVideoRef.current.remove();
        }
      } catch (e) {
        console.warn("Error removing video element:", e);
      }
      screenShareVideoRef.current = null;
    }
  
    // Safely cleanup canvas element
    if (screenShareCanvasRef.current) {
      try {
        // Only remove if it's in the DOM
        if (screenShareCanvasRef.current.parentNode) {
          screenShareCanvasRef.current.remove();
        }
      } catch (e) {
        console.warn("Error removing canvas element:", e);
      }
      screenShareCanvasRef.current = null;
    }
  
    // Safely cleanup share element
    if (shareElementRef.current) {
      try {
        // Only remove if it's in the DOM and has a parent
        if (
          shareElementRef.current.parentNode &&
          document.contains(shareElementRef.current)
        ) {
          shareElementRef.current.remove();
        }
      } catch (e) {
        console.warn("Error removing share element:", e);
      }
      shareElementRef.current = null;
    }
  
    // Safely cleanup container content
    if (shareContainerRef.current) {
      try {
        // Only clear if container is still in DOM
        if (document.contains(shareContainerRef.current)) {
          shareContainerRef.current.innerHTML = "";
        }
      } catch (e) {
        console.warn("Error clearing container:", e);
      }
    }
  }, []);

  const resetScreenShareState = React.useCallback(() => {
    isScreenSharingRef.current = false;
    currentSharingUserIdRef.current = null;
    setScreenSubscribed(false);
    setScreenshareEnabled(false);
    setActiveSharingUserId(null);
    setSharerName("");
    setMediaState((prev) => ({ ...prev, screen: false }));
    cleanupScreenShareElements();
  }, [cleanupScreenShareElements]);

  const subscribeToScreenShareInternal = React.useCallback(
    async (userId, streamInstance) => {
      if (!streamInstance || !shareContainerRef.current) {
        console.warn("Stream or container not ready for subscription");
        return;
      }

      // Check if already subscribed to this user
      if (currentSharingUserIdRef.current === userId) {
        console.log("⏭️ Already subscribed to this user, skipping");
        return;
      }

      // Prevent duplicate subscriptions
      if (isSubscribingRef.current) {
        console.log("⏭️ Already subscribing, skipping duplicate call");
        return;
      }

      try {
        isSubscribingRef.current = true;
        currentSharingUserIdRef.current = userId;

        console.log(`📺 Subscribing to screen share from user ${userId}`);
        console.log("Browser info:", browserInfo);

        // Cleanup existing elements
        if (shareContainerRef.current) {
          shareContainerRef.current.innerHTML = "";
        }

        await new Promise((resolve) => setTimeout(resolve, 100));

        let shareElement;

        if (
          browserInfo.isWebCodecs &&
          !browserInfo.isSafari &&
          !browserInfo.isIOS
        ) {
          console.log("Using VIDEO element (WebCodecs supported)");
          shareElement = document.createElement("video");
          shareElement.id = `screen-share-video-${userId}`;
          shareElement.autoplay = true;
          shareElement.playsInline = true;
          shareElement.muted = true;
        } else {
          console.log("Using CANVAS element (Safari/iOS or no WebCodecs)");
          shareElement = document.createElement("canvas");
          shareElement.id = `screen-share-canvas-${userId}`;
          shareElement.width = 1920;
          shareElement.height = 1080;
        }

        shareElement.style.width = "100%";
        shareElement.style.height = "100%";
        shareElement.style.objectFit = "contain";
        shareElement.style.backgroundColor = "#000";

        if (shareContainerRef.current) {
          shareContainerRef.current.appendChild(shareElement);
        }
        shareElementRef.current = shareElement;
        console.log("Start subscription to screen share", {
          shareElement,
          userId,
        });
        await streamInstance.startShareView(shareElement, userId);
        console.log(
          `✅ Successfully subscribed to screen share using ${shareElement.tagName}`
        );

        setScreenSubscribed(true);
      } catch (err) {
        console.error("❌ Error subscribing to screen share:", err);
        currentSharingUserIdRef.current = null; // Reset on error

        if (shareElementRef.current?.tagName === "VIDEO") {
          console.log("Trying canvas fallback...");
          try {
            if (shareContainerRef.current) {
              shareContainerRef.current.innerHTML = "";
            }

            const canvas = document.createElement("canvas");
            canvas.id = `screen-share-canvas-${userId}`;
            canvas.width = 1920;
            canvas.height = 1080;
            canvas.style.width = "100%";
            canvas.style.height = "100%";
            canvas.style.objectFit = "contain";
            canvas.style.backgroundColor = "#000";

            if (shareContainerRef.current) {
              shareContainerRef.current.appendChild(canvas);
            }
            shareElementRef.current = canvas;

            await streamInstance.startShareView(canvas, userId);
            console.log("✅ Canvas fallback successful");
            currentSharingUserIdRef.current = userId; // Set after success
            setScreenSubscribed(true);
          } catch (fallbackErr) {
            console.error("❌ Canvas fallback also failed:", fallbackErr);
            if (shareContainerRef.current) {
              shareContainerRef.current.innerHTML =
                '<div class="text-white text-center p-4">Failed to load screen share</div>';
            }
            setScreenSubscribed(false);
          }
        } else {
          if (shareContainerRef.current) {
            shareContainerRef.current.innerHTML =
              '<div class="text-white text-center p-4">Failed to load screen share</div>';
          }
          setScreenSubscribed(false);
        }
      } finally {
        // Clear the flag after a delay
        subscriptionTimeoutRef.current = setTimeout(() => {
          isSubscribingRef.current = false;
        }, 1000);
      }
    },
    [browserInfo]
  );

  const unsubscribeFromScreenShare = React.useCallback(async () => {
    const currentStream = streamRef.current;
    console.log(
      "streaming unsubscribing from screen share",
      currentStream,
      stream
    );

    if (!currentStream) return;
    try {
      console.log("🛑 Unsubscribing from screen share");
      await currentStream.stopShareView();
      cleanupScreenShareElements();
      setScreenSubscribed(false);
      currentSharingUserIdRef.current = null; // Reset tracking
      console.log("✅ Successfully unsubscribed from screen share");
    } catch (err) {
      console.error("Error unsubscribing from screen share:", err);
      // Force cleanup anyway
      cleanupScreenShareElements();
      setScreenSubscribed(false);
      currentSharingUserIdRef.current = null;
    }
  }, [cleanupScreenShareElements]);

  React.useEffect(() => {
    let mounted = true;
    const isMobileBrowser = () => {
      const ua = navigator.userAgent.toLowerCase();
      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        ua
      );
    };

    const getDeviceInfo = () => {
      const ua = navigator.userAgent.toLowerCase();
      const isMobile = isMobileBrowser();

      let platform = "desktop";
      if (/iphone|ipod/.test(ua)) platform = "ios-mobile";
      else if (/ipad/.test(ua)) platform = "ios-tablet";
      else if (/android/.test(ua)) {
        platform = /mobile/.test(ua) ? "android-mobile" : "android-tablet";
      }

      return {
        isMobile,
        platform,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        userAgent: navigator.userAgent,
      };
    };
    const initializeZoom = async () => {
      try {
        setIsInitializing(true);
        setError(null);

        // Only create client once
        if (zoomClientRef.current) {
          console.log("Zoom client already exists, skipping initialization");
          return;
        }

        if (!window.isSecureContext) {
          throw new Error("Secure context (HTTPS) required for video calls.");
        }

        if (!navigator.mediaDevices) {
          throw new Error("Media devices not supported.");
        }

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          console.log("iOS detected - ensuring media permissions");
          try {
            const testStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            });
            testStream.getTracks().forEach((track) => track.stop());
          } catch (iosError) {
            console.warn("iOS media permission test failed:", iosError);
          }
        }

        console.log("Initializing Zoom for role:", role);
        const ZoomVideo = (await import("@zoom/videosdk")).default;

        const zoomClient = ZoomVideo.createClient();
        zoomClientRef.current = zoomClient;
        console.log("Zoom client created");

        if (!mounted) return;

        zoomClient.on("connection-change", (payload) => {
          console.log("Connection state changed:", payload);
          if (payload.reason === "ended by host") {
            handleSessionEnd();
          }
        });

        await zoomClient.init("en-US", "Global", {
          patchJsMedia: true,
          leaveOnPageUnload: true,
        });
        console.log("Zoom initialized");

        if (!mounted) return;

        const displayName = role;
        const userContextData = getDeviceInfo();
        console.log("Zoom joined", JSON.stringify(userContextData));

        await zoomClient.join(
          config.sessionName,
          config.sessionToken,
          displayName,
          "",
          { devicename: "Web" }
        );
        if (!mounted) return;

        const mediaStream = zoomClient.getMediaStream();
        setClient(zoomClient);
        setStream(mediaStream);
        streamRef.current = mediaStream;

        const allUsers = zoomClient.getAllUser();
        const selfId = zoomClient.getSessionInfo().userId;

        const initialParticipants = allUsers.map((user) => ({
          userId: user.userId,
          displayName: user.displayName || "User",
          bVideoOn: user.bVideoOn || false,
          muted: user.muted !== false,
        }));

        setParticipants(initialParticipants);

        // Check for existing screen share
        const sharingUser = allUsers.find((u) => u.sharerOn === true);
        if (sharingUser && sharingUser.userId !== selfId) {
          console.log(
            "📺 Found existing screen share from:",
            sharingUser.userId
          );
          setActiveSharingUserId(sharingUser.userId);
          setSharerName(sharingUser.displayName || "User");
          setTimeout(
            () =>
              subscribeToScreenShareInternal(sharingUser.userId, mediaStream),
            1000
          );
        }

        // Initialize audio
        const initAudio = async () => {
          try {
            mediaStream
              .startAudio()
              .then(async () => {
                await mediaStream.muteAudio();
                setMediaState((prev) => ({ ...prev, audio: false }));
                console.log("✅ Audio initialized successfully");
              })
              .catch((audioErr) => {
                console.error("Failed to auto-start audio:", audioErr);
                if (
                  audioErr.errorCode === 6013 ||
                  audioErr.errorCode === 6010
                ) {
                  setAudioDisabled(true);
                }
              });
          } catch (err) {
            console.error("Audio init error:", err);
          }
        };
        initAudio();

        if (onJoin) onJoin();

        // Event listeners
        zoomClient.on("user-added", async (payload) => {
          const userInfo = zoomClientRef.current.getUser(payload.userId);
          console.log("User device info:", userInfo?.deviceName || "Not set");

          const users = Array.isArray(payload) ? payload : [payload];
          console.log("device type", payload);
          setParticipants((prev) => {
            let participants = [...prev];
            users.forEach((user) => {
              if (!participants.find((p) => p.userId === user.userId)) {
                participants.push({
                  userId: user.userId,
                  displayName: user.displayName || "User",
                  bVideoOn: user.bVideoOn || false,
                  muted: user.muted !== false,
                  isDisconnected: false,
                  isPhoneUser: user.isPhoneUser || false,
                });
              }
            });
            return participants;
          });
        });

        zoomClient.on("user-removed", (payload) => {
          console.log("user-removed event payload:", payload);
          const users = Array.isArray(payload) ? payload : [payload];

          setParticipants((prev) =>
            prev.filter((p) => !users.find((u) => u.userId === p.userId))
          );
        });

        zoomClient.on("peer-video-state-change", async (payload) => {
          const { action, userId } = payload;
          setParticipants((prev) =>
            prev.map((p) =>
              p.userId === userId ? { ...p, bVideoOn: action === "Start" } : p
            )
          );
        });

        zoomClient.on("user-updated", (payload) => {
          const users = Array.isArray(payload) ? payload : [payload];
          setParticipants((prev) =>
            prev.map((p) => {
              const updatedUser = users.find((u) => u.userId === p.userId);
              if (updatedUser) {
                return {
                  ...p,
                  bVideoOn:
                    updatedUser.bVideoOn !== undefined
                      ? updatedUser.bVideoOn
                      : p.bVideoOn,
                  muted:
                    updatedUser.muted !== undefined
                      ? updatedUser.muted
                      : p.muted,
                  isDisconnected: updatedUser.isInFailover === true,
                };
              }
              return p;
            })
          );
        });

        zoomClient.on("active-share-change", async (payload) => {
          const selfId = zoomClient.getSessionInfo().userId;
          console.log("🎯 active-share-change:", {
            event: payload,
            selfId: selfId,
            isSelfSharing: payload.userId === selfId,
            currentSharingUser: currentSharingUserIdRef.current,
          });

          try {
            if (payload.state === "Active") {
              console.log("✅ Active share detected, userId:", payload.userId);
              const sharingUser = zoomClient
                .getAllUser()
                .find((u) => u.userId === payload.userId);

              setActiveSharingUserId(payload.userId);
              setSharerName(sharingUser?.displayName || "User");

              if (payload.userId === selfId) {
                console.log("✅ This is your own share");
                setMediaState((prev) => ({ ...prev, screen: true }));
                isScreenSharingRef.current = true;
                return;
              }

              // Only subscribe if not already subscribed to this user
              if (currentSharingUserIdRef.current !== payload.userId) {
                console.log(
                  "📺 Subscribing to remote share from active-share-change..."
                );
                await subscribeToScreenShareInternal(
                  payload.userId,
                  mediaStream
                );
              } else {
                console.log("⏭️ Already subscribed to this user");
              }
            } else if (payload.state === "Inactive") {
              console.log("🛑 Share stopped by:", payload.userId);

              const wasSelfSharing =
                payload.userId === selfId || isScreenSharingRef.current;

              if (wasSelfSharing) {
                console.log("🛑 Your own share stopped");
                resetScreenShareState();
              } else {
                await unsubscribeFromScreenShare();
                setActiveSharingUserId(null);
                setSharerName("");
                setMediaState((prev) => ({ ...prev, screen: false }));
              }
            }
          } catch (err) {
            console.error("⚠️ Error in active-share-change handler:", err);
          }
        });

        zoomClient.on("passively-stop-share", async (payload) => {
          console.log("🛑 passively-stop-share event:", payload);
          const selfId = zoomClient.getSessionInfo().userId;

          try {
            if (payload.userId === selfId) {
              console.log("🛑 Your screen share was passively stopped");
              resetScreenShareState();

              // Show notification
              message.info({
                content: (
                  <div style={{ textAlign: "center" }}>
                    Screen sharing stopped
                  </div>
                ),
                className: "atdclass",
                style: { marginTop: "2vh", color: "#007acc" },
              });

              console.log("✅ Cleaned up after passive stop", {
                isScreenSharingRef: isScreenSharingRef.current,
                mediaStateScreen: false,
              });
            } else {
              // Someone else's share was passively stopped
              console.log("🛑 Remote screen share was passively stopped");
              setTimeout(() => {
                console.log(
                  "🛑🛑🛑Re-enabling screenshare button after passive stop"
                );
                setScreenshareEnabled((prev) => !prev);
              }, 3000); // Call after 3 seconds
              await unsubscribeFromScreenShare();
              setActiveSharingUserId(null);
              setSharerName("");
              setMediaState((prev) => ({ ...prev, screen: false }));
            }
          } catch (err) {
            console.error("⚠️ Error handling passively-stop-share:", err);
            // Force cleanup anyway
            if (mounted) {
              resetScreenShareState();
            }
          }
        });

        zoomClient.on("peer-share-state-change", async (payload) => {
          console.log("🎯 peer-share-state-change:", payload);
          const { userId, action } = payload;
          const selfId = zoomClient.getSessionInfo().userId;

          // Ignore our own share state changes
          if (userId === selfId) {
            console.log("Ignoring self peer-share-state-change");
            return;
          }

          try {
            if (action === "Start") {
              // Check if not already subscribing or subscribed
              console.log("peer states", {
                isSubscribingRef: isSubscribingRef.current,
                currentSharingUserIdRef: currentSharingUserIdRef.current,
                userId,
              });
              if (
                !isSubscribingRef.current &&
                currentSharingUserIdRef.current !== userId
              ) {
                console.log(
                  "📺 Peer started sharing - initiating subscription with delay"
                );

                const sharingUser = zoomClient
                  .getAllUser()
                  .find((u) => u.userId === userId);
                setActiveSharingUserId(userId);
                setSharerName(sharingUser?.displayName || "User");

                // Wait for stream to be ready before subscribing
                // setTimeout(async () => {
                //   // Double-check we're not already subscribed
                //   if (currentSharingUserIdRef.current !== userId) {
                //     console.log(
                //       "⏰ Executing delayed subscription from peer-share-state-change"
                //     );
                //     await subscribeToScreenShareInternal(userId, mediaStream);
                //   }
                // }, 1000); // 1 second delay to ensure stream is ready

                setTimeout(async () => {
                  // Double-check we're not already subscribed AND component is still mounted
                  if (currentSharingUserIdRef.current !== userId && mounted) {
                    console.log(
                      "⏰ Executing delayed subscription from peer-share-state-change"
                    );
                    await subscribeToScreenShareInternal(userId, mediaStream);
                  }
                }, 1000);

              } else {
                console.log(
                  "⏭️ Already subscribing/subscribed, skipping peer-share-state-change"
                );
              }
            // } else if (action === "Stop") {
            //   console.log("🛑 Peer stopped sharing");
            //   await unsubscribeFromScreenShare();
            //   setActiveSharingUserId(null);
            //   setSharerName("");
            //   setMediaState((prev) => ({ ...prev, screen: false }));
            // }
          } else if (action === "Stop") {
            console.log("🛑 Peer stopped sharing");
            
            // SAFE CLEANUP: Check if elements still exist before removing
            if (shareContainerRef.current && shareElementRef.current) {
              // Check if the element is still a child of the container
              if (shareContainerRef.current.contains(shareElementRef.current)) {
                await unsubscribeFromScreenShare();
              } else {
                // Element was already removed by React, just cleanup state
                console.log("📝 Element already removed, cleaning up state only");
                shareElementRef.current = null;
              }
            } else {
              // No elements to cleanup, just update state
              console.log("📝 No elements to cleanup");
            }
            
            // Always update state
            setActiveSharingUserId(null);
            setSharerName("");
            setMediaState((prev) => ({ ...prev, screen: false }));
          }
        } catch (err) {
          console.error("⚠️ Error in peer-share-state-change handler:", err);
          // Don't throw, just log and cleanup state
          setActiveSharingUserId(null);
          setSharerName("");
          setMediaState((prev) => ({ ...prev, screen: false }));
        }
        });
        zoomClient.on("video-aspect-ratio-change", (payload) => {
          console.log("Video aspect ratio changed:", payload);
        });
        setIsInitializing(false);
      } catch (err) {
        console.error("Zoom initialization error:", err);
        if (mounted) {
          setError(err.reason || "Failed to join consultation.");
          setIsInitializing(false);
        }
      }
    };

    initializeZoom();

    return () => {
      mounted = false;
      if (subscriptionTimeoutRef.current) {
        clearTimeout(subscriptionTimeoutRef.current);
      }
      isSubscribingRef.current = false;
      currentSharingUserIdRef.current = null;
      const mediaStream = stream;
      const zoomClient = zoomClientRef.current;

      if (mediaStream) {
        try {
          mediaStream?.stopVideo();
          mediaStream?.stopAudio();
          if (mediaState.screen) {
            mediaStream?.stopShareScreen();
          }
          if (mediaStream?.stopShareView) {
            mediaStream?.stopShareView();
          }
        } catch (e) {
          console.error("Cleanup error:", e);
        }
      }
      if (zoomClient) {
        try {
          zoomClient.leave();
          zoomClientRef.current = null;
        } catch (e) {
          console.error("Leave error:", e);
        }
      }
      cleanupScreenShareElements();
    };
  }, []);
  // }, [config, role, onJoin]);

  // UPDATED: Subscribe to screen share with browser detection
  const subscribeToScreenShare = React.useCallback(
    async (userId) => {
      if (!stream || !shareContainerRef.current) {
        console.warn("Stream or container not ready for subscription");
        return;
      }
      await subscribeToScreenShareInternal(userId, stream);
    },
    [stream, subscribeToScreenShareInternal]
  );

  const handleSessionEnd = async () => {
    console.log("🔚 Session ended");
    try {
      if (stream) {
        await stream.stopVideo();
        await stream.stopAudio();
        if (mediaState.screen) {
          await stream.stopShareScreen();
        }
      }
      if (client) {
        console.log("leave true is called handleSessionEnd");
        await client.leave(true);
      }
    } catch (err) {
      console.error("Error leaving session:", err);
    } finally {
      onLeave();
    }
  };

  const handleToggleVideo = async () => {
    if (!stream || !client) return;
    try {
      const selfId = client.getSessionInfo().userId;
      if (mediaState.video) {
        await stream.stopVideo();
        setMediaState((prev) => ({ ...prev, video: false }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, bVideoOn: false } : p))
        );
      } else {
        // await stream.startVideo();
        await stream.startVideo({ originalRatio: true });
        setMediaState((prev) => ({ ...prev, video: true }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, bVideoOn: true } : p))
        );
      }
    } catch (err) {
      console.error("Error toggling video:", err);
      message.info({
        content: (
          <div style={{ textAlign: "center" }}>
            Please allow permission for camera
          </div>
        ),
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
    }
  };

  const handleToggleAudio = async () => {
    if (!stream) return;
    try {
      const selfId = client?.getSessionInfo().userId;
      if (mediaState.audio) {
        await stream.muteAudio();
        setMediaState((prev) => ({ ...prev, audio: false }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, muted: true } : p))
        );
      } else {
        try {
          await stream.startAudio();
        } catch (e) {
          console.log("Audio already started");
        }
        await stream.unmuteAudio();
        setMediaState((prev) => ({ ...prev, audio: true }));
        setParticipants((prev) =>
          prev.map((p) => (p.userId === selfId ? { ...p, muted: false } : p))
        );
      }
    } catch (err) {
      console.error("Error toggling audio:", err);
      message.info({
        content: (
          <div style={{ textAlign: "center" }}>
            Please allow permission for audio
          </div>
        ),
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
    }
  };

  const handleScreenShare = async () => {
    console.log("handleScreenShare invoked", canShareScreen);
    setShowSidePanel(false);
    if (!canShareScreen) {
      message.info({
        content: (
          <div style={{ textAlign: "center" }}>
            Screen sharing is not available for patients
          </div>
        ),
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
      return;
    }

    if (!stream || !client) {
      console.error("Stream or client not ready");
      return;
    }

    try {
      const selfId = client.getSessionInfo().userId;

      // STOPPING SCREEN SHARE
      if (mediaState.screen || isScreenSharingRef.current) {
        console.log("🛑 Stopping screen share (programmatic)...");

        try {
          // Stop the screen share stream
          await stream.stopShareScreen();
          console.log("✅ stopShareScreen completed");

          // Small delay to ensure event propagates
          await new Promise((resolve) => setTimeout(resolve, 200));

          // Update state immediately
          resetScreenShareState();

          console.log("✅ Screen share stopped successfully");
        } catch (stopErr) {
          console.error("❌ Error stopping screen share:", stopErr);
          // Force cleanup even if stop fails
          resetScreenShareState();
        }

        return;
      }

      // STARTING SCREEN SHARE
      console.log("🔄 Starting screen share...");
      console.log("Browser info:", browserInfo);

      const allUsers = client.getAllUser();
      const someoneSharing = allUsers.some(
        (u) => u.sharerOn === true && u.userId !== selfId
      );

      if (someoneSharing) {
        message.info({
          content: (
            <div style={{ textAlign: "center" }}>
              Someone else is already sharing. Please wait.
            </div>
          ),
          className: "atdclass",
          style: { marginTop: "2vh", color: "#007acc" },
        });
        return;
      }

      // Cleanup any existing elements first
      cleanupScreenShareElements();

      // Small delay before creating new element
      await new Promise((resolve) => setTimeout(resolve, 100));

      let shareElement;

      if (
        browserInfo.isWebCodecs &&
        !browserInfo.isSafari &&
        !browserInfo.isIOS
      ) {
        console.log("Using VIDEO element for sharing");

        const video = document.createElement("video");
        video.id = "my-screen-share-video";
        video.height = 1080;
        video.width = 1920;
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        video.style.cssText = `
          width: 100%;
          height: 100%;
          object-fit: contain;
          position: absolute;
          top: 0;
          left: 0;
          visibility: hidden;
        `;

        document.body.appendChild(video);
        screenShareVideoRef.current = video;
        shareElement = video;
      } else {
        console.log("Using CANVAS element for sharing");

        const canvas = document.createElement("canvas");
        canvas.id = "my-screen-share-canvas";
        canvas.height = 1080;
        canvas.width = 1920;
        canvas.style.cssText = `
          width: 100%;
          height: 100%;
          object-fit: contain;
          position: absolute;
          top: 0;
          left: 0;
          visibility: hidden;
        `;

        document.body.appendChild(canvas);
        screenShareCanvasRef.current = canvas;
        shareElement = canvas;
      }

      console.log("📦 Created share element:", shareElement.tagName);

      // Start screen share
      await stream.startShareScreen(shareElement);
      console.log("✅ startShareScreen completed");

      // Update state immediately after starting
      isScreenSharingRef.current = true;
      setMediaState((prev) => ({ ...prev, screen: true }));
      setScreenshareEnabled(true);
      setActiveSharingUserId(selfId);
      setSharerName("You");

      // Only render the preview in the dedicated container for remote shares.
      if (shareContainerRef.current) {
        shareElement.style.visibility = "visible";
        shareContainerRef.current.appendChild(shareElement);
      }

      console.log(
        `✅ Screen share started successfully with ${shareElement.tagName}`
      );
    } catch (err) {
      console.error("❌ Screen share error:", err);

      // Reset state on error
      resetScreenShareState();

      let errorMessage = "Failed to share screen";
      if (err.type === "INVALID_PARAMETERS") {
        errorMessage =
          "Screen sharing configuration error. Please refresh and try again.";
      } else if (
        err.type === "INSUFFICIENT_PRIVILEGES" ||
        (err.reason && err.reason.includes("permission"))
      ) {
        errorMessage = "Screen sharing permission denied by browser";
      } else if (err.type === "IMPROPER_MEETING_STATE") {
        errorMessage =
          "Session not ready for screen sharing. Please try again.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      message.info({
        content: <div style={{ textAlign: "center" }}>{errorMessage}</div>,
        className: "atdclass",
        style: { marginTop: "2vh", color: "#007acc" },
      });
    }
  };
  const handleLeave = async (isEndByDuplicate = false) => {
    try {
      if (stream) {
        await stream.stopVideo();
        await stream.stopAudio();
        if (mediaState.screen) {
          await stream.stopShareScreen();
        }
      }
      if (client) {
        await client.leave();
      }
    } catch (err) {
      console.error("Error leaving session:", err);
    } finally {
      resetScreenShareState();
      onLeave(isEndByDuplicate);
    }
  };

  const handleEndSession = async () => {
    try {
      if (stream) {
        if (mediaState.video) await stream.stopVideo();
        if (mediaState.audio) await stream.stopAudio();
        if (mediaState.screen) {
          await stream.stopShareScreen();
        }
      }
      if (client) {
        console.log("leave true is called handleEndSession");
        await client.leave(true);
      }
    } catch (err) {
      console.error("Error leaving session:", err);
    } finally {
      resetScreenShareState();
      onLeave();
    }
  };

  const handleRemoveDisconnectedUser = async (userId) => {
    console.log(`🗑️ Removing disconnected user ${userId}`);
    try {
      if (isDoctor && client) {
        const currentUser = client.getCurrentUserInfo();
        if (currentUser.isHost || role === "Web_Doctor") {
          await client.removeUser(userId);
          console.log(`✅ User ${userId} removed from Zoom session`);
        }
      }
      setParticipants((prev) => prev.filter((p) => p.userId !== userId));
    } catch (error) {
      console.error(`❌ Error removing user ${userId}:`, error);
      setParticipants((prev) => prev.filter((p) => p.userId !== userId));
    }
  };

  if (error) {
    return (
      <div className="video-consultation">
        <div className="error-state">
          <div className="error-container">
            <div className="error-icon">
              <AlertCircle className="alert-icon" />
            </div>
            <h3 className="error-title">Connection Error</h3>
            <p className="error-message">{error}</p>
            <button onClick={onLeave} className="error-button">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isInitializing) {
    return (
      <div className="zoom-full-height">
        <div className="loading-state">
          <div className="loading-content">
            <div className="spinner"></div>
            <p className="loading-text">Initializing consultation...</p>
            <p className="loading-subtext">Setting up your video session</p>
          </div>
        </div>
      </div>
    );
  }

  const userId = client?.getCurrentUserInfo()?.userId;
  const selfId = client?.getSessionInfo()?.userId;
  const shouldShowScreenShareLayout =
    activeSharingUserId !== null && activeSharingUserId !== selfId;
  const otherParticipants = participants.filter((p) => p.userId !== userId);
  const isTwoUserLayout = participants.length === 2;

  const getGridClass = (count) => {
    if (count === 1) return "grid-1";
    if (count === 2) return "grid-2";
    if (count === 3) return "grid-3";
    if (count === 4) return "grid-4";
    if (count <= 6) return "grid-5-6";
    return "grid-7-plus";
  };

  const getHeaderInfo = () => {
    const isDoctor = role === "Web_Doctor";
    if (participantCount <= 1) {
      return null;
    }

    if (isDoctor) {
      return {
        name: headerData?.patientName || "Patient",
        age: headerData?.age,
        gender: headerData?.gender,
        consultationDetails: {
          patientName: headerData?.patientName,
          doctorName: headerData?.doctorDetails?.doctorName,
          date: headerData?.appointmentDate,
          time: headerData?.appointmentTime,
          modeOfConsultation: headerData?.modeOfConsultation,
          branch: headerData?.branchName,
        },
      };
    } else {
      return {
        name: headerData?.doctorName || "Doctor",
        age: headerData?.doctorDetails?.age,
        gender: headerData?.doctorDetails?.gender,
        consultationDetails: {
          doctorName: headerData?.doctorDetails?.doctorName,
          specialization: headerData?.doctorDetails?.specialization,
          date: headerData?.appointmentDate,
          time: headerData?.appointmentTime,
          modeOfConsultation: headerData?.modeOfConsultation,
          branch: headerData?.branchName,
        },
      };
    }
  };

  const headerInfo = getHeaderInfo();

  return (
    <div className="video-consultation">
      {headerInfo && (
        <ConsultationHeader
          name={headerInfo.name}
          age={headerInfo.age}
          gender={headerInfo.gender}
          consultationDetails={headerInfo.consultationDetails}
          showDetailsButton={false}
        />
      )}

      <div
        className={`video-container-wrapper ${
          showSidePanel ? "with-side-panel" : ""
        }`}
      >
        <div className="main-video-area">
          {shouldShowScreenShareLayout ? (
            <div className="screen-share-layout">
              <div className="screen-share-content">
                {/* <div className="sharing-indicator">
                  <Circle className="recording-icon" fill="currentColor" />
                  <span className="sharer-name">
                    {sharerName} {sharerName === "You"
                      ? "are sharing screen now"
                      : "is sharing screen"}
                  </span>
                </div> */}
                <div ref={shareContainerRef} className="video-player-container">
                  <div className="loading-state">
                    <Loader2 className="spinner" />
                    <p>Loading screen share...</p>
                  </div>
                </div>
              </div>
              <div className="participants-carousel">
                <div className="carousel-container">
                  {stream &&
                    userId &&
                    participants.map((participant, index) => (
                      <div
                        key={participant.userId || index}
                        className="participant-item"
                      >
                        <RemoteVideo
                          stream={stream}
                          userId={participant.userId}
                          isVideoOn={participant.bVideoOn}
                          isAudioOn={!participant.muted}
                          userName={
                            participant.userId === userId
                              ? `You`
                              : participant.displayName || "User"
                          }
                          userData={participant}
                          isDisconnected={participant.isDisconnected}
                          onRemoveDisconnected={handleRemoveDisconnectedUser}
                        />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ) : isTwoUserLayout ? (
            <div className="two-user-layout">
              {mediaState.screen && (
                <div className="self-sharing-indicator" style={{display:"none"}}>
                  <div className="pulse-dot"></div>
                  <Monitor className="monitor-icon" />
                  <span className="sharing-text">
                    You are sharing your screen
                  </span>
                </div>
              )}
              <div className="main-view-container" ref={containerRef}>
                {stream && otherParticipants.length > 0 && (
                  <RemoteVideo
                    stream={stream}
                    userId={otherParticipants[0].userId}
                    isVideoOn={otherParticipants[0].bVideoOn}
                    isAudioOn={!otherParticipants[0].muted}
                    userName={otherParticipants[0].displayName || "User"}
                    userData={otherParticipants[0]}
                    isDisconnected={otherParticipants[0].isDisconnected}
                    onRemoveDisconnected={handleRemoveDisconnectedUser}
                  />
                )}
                {stream && userId && (
                  <div
                    ref={pipRef}
                    className={`pip-container ${isDragging ? "dragging" : ""}`}
                    style={{
                      left: `${pipPosition.x}px`,
                      top: `${pipPosition.y}px`,
                    }}
                    onMouseDown={handleMouseDown}
                  >
                    <RemoteVideo
                      stream={stream}
                      userId={userId}
                      isVideoOn={mediaState.video}
                      isAudioOn={mediaState.audio}
                      isScreenshareOn={screenshareEnabled}
                      userName={`You`}
                      isPip={true}
                      className="pip-content"
                      userData={{}}
                      isDisconnected={false}
                      onRemoveDisconnected={() => {}}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="gallery-layout">
              {mediaState.screen && (
                <div className="gallery-sharing-indicator">
                  <div className="pulse-dot"></div>
                  <Monitor className="monitor-icon" />
                  <span className="sharing-text">
                    You are sharing your screen
                  </span>
                </div>
              )}
              <div className="gallery-container">
                {stream && userId && (
                  <div
                    className={`participants-grid ${getGridClass(
                      participants.length
                    )}`}
                  >
                    {participants.map((participant, index) => (
                      <div
                        key={participant.userId || index}
                        className="participant-grid-item"
                      >
                        <RemoteVideo
                          stream={stream}
                          userId={participant.userId}
                          isVideoOn={participant.bVideoOn}
                          isAudioOn={!participant.muted}
                          userName={
                            participant.userId === userId
                              ? `${role} (You)`
                              : participant.displayName || "User"
                          }
                          userData={participant}
                          isDisconnected={participant.isDisconnected}
                          onRemoveDisconnected={handleRemoveDisconnectedUser}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* <div className="recording-indicator">
            <Circle className="recording-icon" fill="currentColor" />
            Recording in progress
          </div>
          <div className="sharing-indicator">
            <Circle className="recording-icon" fill="currentColor" />
            <span className="sharer-name">
              {sharerName}{" "}
              {sharerName === "You"
                ? "are sharing screen now"
                : "is sharing screen"}
            </span>
          </div> */}
          {/* Replace the existing separate indicator divs with this */}
          <div className="status-indicators-container">
          <div className="recording-indicator">
            <Circle className="recording-icon" fill="currentColor" />
            Recording in progress
          </div>
          {activeSharingUserId && (
            <div className="sharing-indicator">
              <Circle className="recording-icon" fill="currentColor" />
              <span className="sharer-name">
                {sharerName}{" "}
                {sharerName === "You"
                  ? "are sharing screen now"
                  : "is sharing screen"}
              </span>
            </div>
          )}
        </div>
          <ControlBar
            mediaState={mediaState}
            onToggleVideo={handleToggleVideo}
            onToggleAudio={handleToggleAudio}
            onLeave={handleLeave}
            onEndConsultation={isDoctor ? handleEndSession : null}
            onToggleScreenShare={handleScreenShare}
            canShareScreen={isDoctor}
            canLeave={canLeave}
            role={role}
            mediaStream={stream}
            zmClient={client}
            isAudioDisabled={audioDisabled}
            showSidePanel={showSidePanel}
            onToggleSidePanel={(activeTab) => {
              setShowSidePanel(!showSidePanel);
              setActiveTab(activeTab);
            }}
          />
        </div>

        {showSidePanel && (
          <div className="side-panel-area">
            <ConsultationSidePanel
              appointmentData={headerData}
              onClose={() => setShowSidePanel(false)}
              activeTabProp={activeTab}
            />
          </div>
        )}
        {/* <div className="status-indicators-container">
          <div className="recording-indicator">
            <Circle className="recording-icon" fill="currentColor" />
            Recording in progress
          </div>
          {activeSharingUserId && (
            <div className="sharing-indicator">
              <Circle className="recording-icon" fill="currentColor" />
              <span className="sharer-name">
                {sharerName}{" "}
                {sharerName === "You"
                  ? "are sharing screen now"
                  : "is sharing screen"}
              </span>
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default VideoConsultation;
